import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AiwynEditorComponent, AutocompleteComponent } from '@aiwyn/foundations/components';
import {
  AgGridClearColumnFiltersButtonComponent,
  AgGridFactory,
  ConfirmationModalComponent,
  HasuraBaseQuery,
  InlineBannerComponent,
  PageComponent,
  PageContentComponent,
  PageHeaderComponent,
  SharedModule,
  ToggleComponent,
} from '@anduin/shared';
import { NgxsModule } from '@ngxs/store';
import { AgGridModule } from 'ag-grid-angular';
import { Apollo } from 'apollo-angular';
import { DpDatePickerModule } from 'ng2-date-picker';
import {
  EmailDeliveryHubFailedComponent,
  EmailDeliveryHubFiltersComponent,
  EmailDeliveryHubInsightsComponent,
  EmailDeliveryHubMetricsService,
  EmailDeliveryHubPendingComponent,
  EmailDeliveryHubSentTableComponent,
  ResendEmailModalComponent,
  ResendIconCellTemplateComponent,
  ViewAttachmentsCellTemplateComponent,
} from './components';
import { FailedEmailDatasource } from './components/email-delivery-hub-failed/services/failed-datasource.service';
import { FailedGridOptionsFactory } from './components/email-delivery-hub-failed/services/failed-grid-options.factory';
import { FAILED_HASURA_BASE_QUERY, FAILED_HASURA_BASE_QUERY_TOKEN } from './components/email-delivery-hub-failed/utils/hasura-base-query';
import { PendingEmailDatasource } from './components/email-delivery-hub-pending/services/pending-datasource.service';
import { PendingGridOptionsFactory } from './components/email-delivery-hub-pending/services/pending-grid-options.factory';
import {
  PENDING_HASURA_BASE_QUERY,
  PENDING_HASURA_BASE_QUERY_TOKEN,
} from './components/email-delivery-hub-pending/utils/hasura-base-query';
import {
  EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY,
  EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY_TOKEN,
  EmailDeliveryHubAgGridFilterAggregateFactory,
  EmailDeliveryHubColumnsFactory,
  EmailDeliveryHubDataSourceService,
  EmailDeliveryHubGridOptionsFactory,
  SmtpErrorsService,
} from './components/email-delivery-hub-sent-table';
import { EmailDeliveryHubContainerComponent } from './containers';
import { ResponsiveToolbarDirective } from './containers/email-delivery-hub-container/responsive-toolbar.directive';
import { KEY_VALUE_STORAGE, SessionStorageService } from './services';
import { EmailDeliveryHubState } from './state';

@NgModule({
  declarations: [
    EmailDeliveryHubContainerComponent,
    EmailDeliveryHubFiltersComponent,
    EmailDeliveryHubInsightsComponent,
    EmailDeliveryHubSentTableComponent,
    ResendIconCellTemplateComponent,
    ResendEmailModalComponent,
    ViewAttachmentsCellTemplateComponent,
  ],
  imports: [
    SharedModule,
    PageComponent,
    PageHeaderComponent,
    PageContentComponent,
    DpDatePickerModule,
    CommonModule,
    ReactiveFormsModule,
    AutocompleteComponent,
    AgGridModule,
    MatFormFieldModule,
    MatInputModule,
    ConfirmationModalComponent,
    MatCheckboxModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    ToggleComponent,
    AiwynEditorComponent,
    InlineBannerComponent,
    AgGridClearColumnFiltersButtonComponent,
    ResponsiveToolbarDirective,
    NgxsModule.forFeature([EmailDeliveryHubState]),
    EmailDeliveryHubFailedComponent,
    EmailDeliveryHubPendingComponent,
  ],
  providers: [
    EmailDeliveryHubAgGridFilterAggregateFactory,
    EmailDeliveryHubGridOptionsFactory,
    EmailDeliveryHubColumnsFactory,
    EmailDeliveryHubMetricsService,
    SmtpErrorsService,
    {
      provide: AgGridFactory,
      useValue: new AgGridFactory(),
    },
    {
      provide: EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY_TOKEN,
      useValue: EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY,
    },
    {
      provide: EmailDeliveryHubDataSourceService,
      useFactory: (apollo: Apollo, query: HasuraBaseQuery) => new EmailDeliveryHubDataSourceService(apollo, query),
      deps: [Apollo, EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY_TOKEN],
    },
    {
      provide: KEY_VALUE_STORAGE,
      useClass: SessionStorageService,
    },
    // Pending table
    PendingGridOptionsFactory,
    {
      provide: PENDING_HASURA_BASE_QUERY_TOKEN,
      useValue: PENDING_HASURA_BASE_QUERY,
    },
    {
      provide: PendingEmailDatasource,
      useFactory: (apollo: Apollo, query: HasuraBaseQuery) => new PendingEmailDatasource(apollo, query),
      deps: [Apollo, PENDING_HASURA_BASE_QUERY_TOKEN],
    },
    // Failed table
    FailedGridOptionsFactory,
    {
      provide: FAILED_HASURA_BASE_QUERY_TOKEN,
      useValue: FAILED_HASURA_BASE_QUERY,
    },
    {
      provide: FailedEmailDatasource,
      useFactory: (apollo: Apollo, query: HasuraBaseQuery) => new FailedEmailDatasource(apollo, query),
      deps: [Apollo, FAILED_HASURA_BASE_QUERY_TOKEN],
    },
  ],
  exports: [EmailDeliveryHubContainerComponent],
})
export class EmailDeliveryHubModule {}
